import { useUserInfo } from "@/api/user";
import { config } from "@/config";
import { shouldRecordReplay } from "@/integrations/replayGate";
import Sentry from "@/integrations/Sentry";
import { trackSessionStart } from "@/utils/analytics";
import { useEffect, useRef } from "react";

const REPLAY_RESTART_THROTTLE_MS = 30_000;

// Mixpanel identity lives in AnalyticsIdentitySync; this keeps GTM + Sentry.
export function useTrackUser() {
  const { data: userInfo } = useUserInfo();
  const hasTrackedSessionStart = useRef(false);
  const lastReplayStart = useRef(-Infinity);

  useEffect(() => {
    if (userInfo) {
      // Track session start only once
      if (!hasTrackedSessionStart.current) {
        trackSessionStart({ userId: userInfo.user.id });
        hasTrackedSessionStart.current = true;
      }

      Sentry.setUser({
        email: userInfo.user.email,
        id: userInfo.user.id,
        organization_id: userInfo.organization?.id,
        organization: userInfo.organization?.name,
      });

      Sentry.setTags({
        user_id: userInfo.user.id,
        user_name: userInfo.user.name,
        user_email: userInfo.user.email,
        organization_id: userInfo.organization?.id,
        organization_name: userInfo.organization?.name,
      });

      const email = userInfo.user.email;
      // With both replay sample rates at 0, the SDK stops for good when a
      // session expires (15 min idle / 60 min max); start a new one.
      const startReplayIfStopped = () => {
        if (
          Sentry.getReplay()?.getReplayId() !== undefined ||
          !shouldRecordReplay({
            enabled: config.sentry.enabled,
            environment: config.sentry.environment,
            email,
            webdriver: navigator.webdriver,
            pathname: window.location.pathname,
          }) ||
          Date.now() - lastReplayStart.current < REPLAY_RESTART_THROTTLE_MS
        ) {
          return;
        }
        lastReplayStart.current = Date.now();
        try {
          Sentry.getReplay()?.start();
        } catch {
          // already recording
        }
      };
      startReplayIfStopped();

      // Deferred: the SDK only notices the expiry in its own handler for the
      // same event (click, visibilitychange), until then the stale id stays.
      let pending: ReturnType<typeof setTimeout> | undefined;
      const onActivity = () => {
        if (document.visibilityState !== "visible") return;
        clearTimeout(pending);
        pending = setTimeout(startReplayIfStopped);
      };
      const events = ["click", "keydown", "visibilitychange"] as const;
      for (const event of events) {
        document.addEventListener(event, onActivity);
      }
      return () => {
        clearTimeout(pending);
        for (const event of events) {
          document.removeEventListener(event, onActivity);
        }
      };
    }
  }, [userInfo]);
}
